import { observable, makeObservable, action, computed } from 'mobx'

/**
 * A wrapper around MobX's observable.map that ensures all mutations are performed within MobX actions.
 *
 * It stops the madness of having to wrap every ObservableMap state mutation in `runInAction`.
 *
 * In MobX strict mode, modifying observable state outside of an action (such as directly calling `set`, `delete`, or `clear` on an observable map)
 * will throw errors or warnings. This often leads to the need to wrap such mutations in `runInAction`, which can clutter code and make it harder to maintain.
 *
 * This class provides a safer and more ergonomic API by exposing all mutating methods (`set`, `delete`, `clear`, `replace`) as MobX actions,
 * so you don't need to manually use `runInAction` when updating the map. This helps prevent accidental state modifications outside actions
 * and makes working with observable maps more straightforward.
 */
export class ObservableMapStore<T extends object> {
  @observable
  map = observable.map<keyof T, T[keyof T]>()

  constructor(initialValue?: T) {
    makeObservable(this)

    if (initialValue) {
      Object.entries(initialValue).forEach(([key, value]) => {
        this.map.set(key as keyof T, value as T[keyof T])
      })
    }
  }

  @computed
  get keys() {
    return Array.from(this.map.keys()) as (keyof T)[]
  }

  @computed
  get values() {
    return Array.from(this.map.values()) as T[keyof T][]
  }

  get<K extends keyof T>(key: K): T[K] | undefined {
    return this.map.get(key) as T[K] | undefined
  }

  has<K extends keyof T>(key: K): boolean {
    return this.map.has(key)
  }

  @computed
  get size(): number {
    return this.map.size
  }

  @action
  set<K extends keyof T>(key: K, value: T[K]) {
    this.map.set(key, value)
  }

  @action
  clear() {
    this.map.clear()
  }

  @action
  replace(newMap: Partial<T>) {
    this.map.clear()
    this.map.replace(newMap as Record<keyof T, T[keyof T]>)
  }

  @action
  delete<K extends keyof T>(key: K) {
    this.map.delete(key)
  }
}
